/**
 * CreateMenu.jsx — the `+` popover next to the mic that opens Rammy's
 * generative capabilities.
 *
 * v2 = image + short video (Sora 2). Edit lives on the image bubble itself.
 * Sound/ambience is the next drop.
 */
import { useEffect, useRef, useState } from "react";
import { Plus, Image as ImageIcon, Loader2, Film, Music } from "lucide-react";

const VIDEO_ASPECTS = [
  { key: "landscape", label: "Landscape", size: "1280x720",  hint: "16:9" },
  { key: "portrait",  label: "Portrait",  size: "1024x1792", hint: "9:16" },
  { key: "square",    label: "Square",    size: "1024x1024", hint: "1:1"  },
];
const VIDEO_DURATIONS = [4, 8, 12];

const SOUND_PRESETS = [
  "Gentle rain on a wooden porch, distant thunder",
  "Warm café hum with occasional espresso hiss",
  "Deep forest at dawn, birds waking one by one",
  "Ocean waves rolling onto a quiet cove at night",
  "Crackling fireplace in a cabin, wind outside",
];

export function CreateMenu({ onCreateImage, onCreateVideo, onCreateSound, disabled }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("menu");   // menu | image | video | sound | busy
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("landscape");
  const [duration, setDuration] = useState(4);
  const [soundDur, setSoundDur] = useState(6);
  const [soundLoop, setSoundLoop] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const resetAndClose = () => {
    setPrompt(""); setStep("menu"); setAspect("landscape"); setDuration(4);
    setSoundDur(6); setSoundLoop(false); setOpen(false);
  };

  const submitImage = async () => {
    const p = prompt.trim();
    if (!p || step === "busy") return;
    setStep("busy");
    try {
      await onCreateImage?.(p);
      resetAndClose();
    } catch (_) {
      setStep("image");
    }
  };

  const submitVideo = async () => {
    const p = prompt.trim();
    if (!p || step === "busy") return;
    const size = VIDEO_ASPECTS.find(a => a.key === aspect)?.size || "1280x720";
    setStep("busy");
    try {
      await onCreateVideo?.(p, { size, duration });
      resetAndClose();
    } catch (_) {
      setStep("video");
    }
  };

  const submitSound = async () => {
    const p = prompt.trim();
    if (!p || step === "busy") return;
    setStep("busy");
    try {
      await onCreateSound?.(p, { duration_seconds: soundDur, loop: soundLoop });
      resetAndClose();
    } catch (_) {
      setStep("sound");
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        className={`w-11 h-11 rounded-full border flex items-center justify-center transition disabled:opacity-40
          ${open
            ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]"
            : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)] text-[var(--rammy-text-sec)]"}`}
        aria-label="Create menu"
        data-testid="create-menu-btn"
        aria-expanded={open}
      >
        <Plus size={16} className={open ? "rotate-45 transition-transform" : "transition-transform"}/>
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-3 w-[340px] rounded-2xl bg-[var(--rm-surface)] border border-black/[0.08] shadow-[0_20px_60px_rgba(17,19,24,0.15)] p-3 z-20"
          data-testid="create-menu-popover"
        >
          {step === "menu" && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] px-2 pt-1 pb-2">Create with Rammy</div>
              <button
                type="button"
                onClick={() => setStep("image")}
                className="w-full flex items-center gap-3 rounded-xl px-2.5 py-2.5 hover:bg-[var(--rammy-surface-alt)]/60 text-left transition"
                data-testid="create-menu-image"
              >
                <span className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center">
                  <ImageIcon size={16}/>
                </span>
                <span className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[var(--rammy-text)]">Create an image</div>
                  <div className="text-[12px] text-[var(--rammy-text-muted)]">Turn a description into a picture.</div>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStep("video")}
                className="w-full flex items-center gap-3 rounded-xl px-2.5 py-2.5 hover:bg-[var(--rammy-surface-alt)]/60 text-left transition"
                data-testid="create-menu-video"
              >
                <span className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center">
                  <Film size={16}/>
                </span>
                <span className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[var(--rammy-text)] flex items-center gap-2">
                    Create a short video
                    <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)]/25 text-[var(--rammy-cyan-dark)] rounded-full px-1.5 py-0.5">New</span>
                  </div>
                  <div className="text-[12px] text-[var(--rammy-text-muted)]">4–12 second Sora 2 clip. Takes a few minutes.</div>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStep("sound")}
                className="w-full flex items-center gap-3 rounded-xl px-2.5 py-2.5 hover:bg-[var(--rammy-surface-alt)]/60 text-left transition"
                data-testid="create-menu-sound"
              >
                <span className="w-9 h-9 rounded-xl bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] flex items-center justify-center">
                  <Music size={16}/>
                </span>
                <span className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[var(--rammy-text)] flex items-center gap-2">
                    Sounds &amp; ambience
                    <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)]/25 text-[var(--rammy-cyan-dark)] rounded-full px-1.5 py-0.5">New</span>
                  </div>
                  <div className="text-[12px] text-[var(--rammy-text-muted)]">1–30s clip. Loopable ambience for focus.</div>
                </span>
              </button>
            </div>
          )}

          {step === "image" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <ImageIcon size={14} className="text-[var(--rammy-cyan-dark)]"/>
                <div className="text-[13px] font-medium">Describe an image</div>
              </div>
              <textarea
                rows={3}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitImage(); }}
                placeholder="A warm watercolor of a small round robot beside a steaming mug at sunrise…"
                className="w-full resize-none rounded-xl border border-[var(--rammy-border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rammy-cyan)] focus:ring-2 focus:ring-[var(--rammy-cyan)]/30"
                data-testid="create-image-prompt"
              />
              <FooterActions
                onBack={() => setStep("menu")}
                onSubmit={submitImage}
                submitTestId="create-image-submit"
                disabled={!prompt.trim()}
                label="Create"
              />
              <div className="text-[11px] text-[var(--rammy-text-muted)] px-1">Takes about 15 seconds. Rammy will show it in the chat.</div>
            </div>
          )}

          {step === "video" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <Film size={14} className="text-[var(--rammy-cyan-dark)]"/>
                <div className="text-[13px] font-medium">Describe a short scene</div>
              </div>
              <textarea
                rows={3}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitVideo(); }}
                placeholder="A tiny origami crane unfolding on a wooden desk as morning light crosses the room…"
                className="w-full resize-none rounded-xl border border-[var(--rammy-border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rammy-cyan)] focus:ring-2 focus:ring-[var(--rammy-cyan)]/30"
                data-testid="create-video-prompt"
              />

              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] px-1">Frame</div>
                <div className="flex gap-1.5">
                  {VIDEO_ASPECTS.map(a => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setAspect(a.key)}
                      className={`flex-1 rounded-lg border px-2 py-1.5 text-[12px] font-medium transition
                        ${aspect === a.key
                          ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10 text-[var(--rammy-text)]"
                          : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]/60"}`}
                      data-testid={`create-video-aspect-${a.key}`}
                    >
                      {a.label}
                      <span className="ml-1 text-[10px] opacity-60">{a.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)] px-1">Length</div>
                <div className="flex gap-1.5">
                  {VIDEO_DURATIONS.map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuration(d)}
                      className={`flex-1 rounded-lg border px-2 py-1.5 text-[12px] font-medium transition
                        ${duration === d
                          ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10 text-[var(--rammy-text)]"
                          : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]/60"}`}
                      data-testid={`create-video-duration-${d}`}
                    >
                      {d}s
                    </button>
                  ))}
                </div>
              </div>

              <FooterActions
                onBack={() => setStep("menu")}
                onSubmit={submitVideo}
                submitTestId="create-video-submit"
                disabled={!prompt.trim()}
                label="Weave"
              />
              <div className="text-[11px] text-[var(--rammy-text-muted)] px-1">
                Sora 2 typically takes 2–5 minutes. Rammy will hold the scene for you.
              </div>
            </div>
          )}

          {step === "sound" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <Music size={14} className="text-[var(--rammy-cyan-dark)]"/>
                <div className="text-[13px] font-medium">Describe a sound</div>
              </div>
              <textarea
                rows={2}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitSound(); }}
                placeholder="Gentle rain on a wooden porch, distant thunder…"
                className="w-full resize-none rounded-xl border border-[var(--rammy-border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rammy-cyan)] focus:ring-2 focus:ring-[var(--rammy-cyan)]/30"
                data-testid="create-sound-prompt"
              />
              {/* Presets */}
              <div className="flex flex-wrap gap-1.5">
                {SOUND_PRESETS.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPrompt(p)}
                    className="text-[11px] px-2 py-1 rounded-full border border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]/60 hover:text-[var(--rammy-text)] transition"
                    data-testid={`create-sound-preset-${i}`}
                  >
                    {p.split(",")[0]}
                  </button>
                ))}
              </div>
              {/* Duration */}
              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">Length</div>
                  <div className="text-[12px] font-medium text-[var(--rammy-text)]" data-testid="create-sound-duration-value">{soundDur.toFixed(1)}s</div>
                </div>
                <input
                  type="range"
                  min="1"
                  max="30"
                  step="0.5"
                  value={soundDur}
                  onChange={e => setSoundDur(parseFloat(e.target.value))}
                  className="w-full accent-[var(--rammy-cyan-dark)]"
                  data-testid="create-sound-duration"
                />
              </div>
              {/* Loop toggle */}
              <label className="flex items-center gap-2 text-[12px] text-[var(--rammy-text-sec)] cursor-pointer px-1 select-none">
                <input
                  type="checkbox"
                  checked={soundLoop}
                  onChange={e => setSoundLoop(e.target.checked)}
                  className="accent-[var(--rammy-cyan-dark)]"
                  data-testid="create-sound-loop"
                />
                Seamless loop (for focus/ambience)
              </label>
              <FooterActions
                onBack={() => setStep("menu")}
                onSubmit={submitSound}
                submitTestId="create-sound-submit"
                disabled={!prompt.trim()}
                label="Compose"
              />
              <div className="text-[11px] text-[var(--rammy-text-muted)] px-1">
                Uses your ElevenLabs quota. Usually takes 3–8 seconds.
              </div>
            </div>
          )}

          {step === "busy" && (
            <div className="flex items-center gap-3 p-4">
              <Loader2 size={18} className="animate-spin text-[var(--rammy-cyan-dark)]"/>
              <div className="text-[13px] text-[var(--rammy-text-sec)]">Handing off to Rammy…</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FooterActions({ onBack, onSubmit, submitTestId, disabled, label }) {
  return (
    <div className="flex items-center gap-2 justify-between">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)]"
      >Back</button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[var(--rammy-cyan)] text-[var(--rammy-text)] text-sm font-medium hover:bg-[var(--rammy-cyan-dark)] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={submitTestId}
      >
        {label} <span className="text-[11px] opacity-70">⌘↵</span>
      </button>
    </div>
  );
}
